import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Button,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import Battery60Icon from '@mui/icons-material/Battery60';
import BatteryCharging60Icon from '@mui/icons-material/BatteryCharging60';
import Battery20Icon from '@mui/icons-material/Battery20';
import BatteryCharging20Icon from '@mui/icons-material/BatteryCharging20';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import RouteIcon from '@mui/icons-material/Route';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExploreIcon from '@mui/icons-material/Explore';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SendIcon from '@mui/icons-material/Send';
import MapIcon from '@mui/icons-material/Map';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import EngineIcon from '../../resources/images/data/engine.svg?react';
import { openUrl } from '../util/navigationFallback';
import { buildMapsLinks, reportMapOpenFailure } from '../util/mapLinks';
import { useTranslation } from './LocalizationProvider';

const detectLocked = (device, position) => {
  const devAttrs = device?.attributes || {};
  const posAttrs = position?.attributes || {};

  let raw =
    devAttrs.lock ??
    devAttrs.blocked ??
    devAttrs.bloqueado ??
    devAttrs.engineBlocked ??
    posAttrs.lock ??
    posAttrs.blocked ??
    posAttrs.bloqueado ??
    posAttrs.engineBlocked ??
    device?.locked ??
    device?.blocked ??
    position?.blocked;

  if (raw === undefined && posAttrs.output !== undefined) {
    raw = posAttrs.output;
  }

  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw === 1;
  if (typeof raw === 'string') {
    const v = raw.toLowerCase();
    return (
      v === 'true'
      || v === '1'
      || v === 'locked'
      || v === 'on'
      || v === 'bloqueado'
    );
  }
  return false;
};

const toKph = (knots) => {
  if (knots == null) return null;
  const n = Number(knots);
  if (Number.isNaN(n)) return null;
  return n * 1.852;
};

const hoursSince = (dateLike) => {
  if (!dateLike) return null;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60));
};

const minutesSince = (dateLike) => {
  if (!dateLike) return null;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60));
};

const getVehicleState = (device, position) => {
  const attrs = position?.attributes || {};
  const ignition = attrs.ignition;
  const speedKph = toKph(position?.speed);
  const moving = speedKph != null && speedKph > 2;

  const lastUpdate =
    position?.fixTime
    || position?.deviceTime
    || position?.serverTime
    || position?.time
    || position?.timestamp
    || device?.lastUpdate
    || device?.lastUpdateTime
    || null;

  const h = hoursSince(lastUpdate);
  const noData = !lastUpdate;
  const stale72 = h != null && h >= 72;
  const stale24 = h != null && h >= 24 && h < 72;

  const idle = ignition === true && !moving;
  const idleMinutes = idle ? minutesSince(position?.fixTime || device?.lastUpdate) : null;
  const idleOver10 = idle && idleMinutes != null && idleMinutes >= 10;

  if (noData) {
    return {
      label: 'Sem dados',
      color: '#6C757D',
      icon: <ErrorOutlineIcon fontSize="small" />,
    };
  }

  if (stale72) {
    return {
      label: 'Desconectado > 72h',
      color: '#6F42C1',
      icon: <WifiOffIcon fontSize="small" />,
    };
  }

  if (stale24) {
    return {
      label: 'Desconectado > 24h',
      color: '#0B3D91',
      icon: <WifiOffIcon fontSize="small" />,
    };
  }

  if (ignition === false) {
    return {
      label: 'Ignição desligada',
      color: '#DC3545',
      icon: <EngineIcon width={18} height={18} />,
    };
  }

  if (moving) {
    return {
      label: speedKph != null ? `Em movimento (${Math.round(speedKph)} km/h)` : 'Em movimento',
      color: '#28A745',
      icon: <EngineIcon width={18} height={18} />,
    };
  }

  if (idleOver10) {
    return {
      label: `Ocioso > 10 min${idleMinutes != null ? ` (${idleMinutes} min)` : ''}`,
      color: '#FD7E14',
      icon: <PauseCircleFilledIcon fontSize="small" />,
    };
  }

  if (idle) {
    return {
      label: `Parado com motor ligado${idleMinutes != null ? ` (${idleMinutes} min)` : ''}`,
      color: '#FFC107',
      icon: <PauseCircleFilledIcon fontSize="small" />,
    };
  }

  if (ignition === true) {
    return {
      label: 'Ignição ligada',
      color: '#28A745',
      icon: <EngineIcon width={18} height={18} />,
    };
  }

  return {
    label: 'Status desconhecido',
    color: '#6C757D',
    icon: <ErrorOutlineIcon fontSize="small" />,
  };
};

const safeClipboardWrite = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const BatteryIcon = ({ level, charging, color }) => {
  if (level == null) return null;

  if (level > 70) {
    return charging
      ? <BatteryChargingFullIcon sx={{ color, fontSize: 18 }} />
      : <BatteryFullIcon sx={{ color, fontSize: 18 }} />;
  }

  if (level > 30) {
    return charging
      ? <BatteryCharging60Icon sx={{ color, fontSize: 18 }} />
      : <Battery60Icon sx={{ color, fontSize: 18 }} />;
  }

  return charging
    ? <BatteryCharging20Icon sx={{ color, fontSize: 18 }} />
    : <Battery20Icon sx={{ color, fontSize: 18 }} />;
};

const QuickAction = ({ title, icon, onClick }) => (
  <Button
    variant="outlined"
    size="small"
    startIcon={icon}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(e);
    }}
    sx={{
      minHeight: 36,
      borderRadius: 2,
      textTransform: 'none',
      px: 1.2,
      fontSize: '0.78rem',
    }}
  >
    {title}
  </Button>
);

const MobileDeviceBar = ({
  deviceId,
  position,
  onExpand,
  onClose,
}) => {
  const navigate = useNavigate();
  const t = useTranslation();
  const device = useSelector((state) => state.devices.items[deviceId]);

  const [mapsAnchorEl, setMapsAnchorEl] = useState(null);
  const [toast, setToast] = useState('');
  const [failedMapUrl, setFailedMapUrl] = useState('');

  const state = useMemo(
    () => getVehicleState(device, position),
    [device, position],
  );

  const speedKph = toKph(position?.speed);
  const speedLabel = speedKph == null ? '—' : `${Math.round(speedKph)} km/h`;

  const locked = detectLocked(device, position);
  const battery = position?.attributes?.batteryLevel;
  const charging = position?.attributes?.charge;
  const links = position ? buildMapsLinks(position) : null;

  const openMapLink = (url, provider) => {
    if (!url) return;
    const result = openUrl(url, { preferSameTab: false });
    if (!result.openedInNewTab) {
      setFailedMapUrl(url);
      reportMapOpenFailure({
        source: 'mobile_device_bar',
        provider,
        url,
        deviceId,
        positionId: position?.id,
      });
    }
  };

  const copyCoords = async () => {
    if (!position?.latitude || !position?.longitude) return;
    await safeClipboardWrite(`${position.latitude},${position.longitude}`);
  };

  return (
    <>
      <Box
        sx={{
          px: 1.25,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.9,
          userSelect: 'none',
        }}
      >
        <Box
          onClick={onExpand}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            cursor: 'pointer',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: state.color,
                flexShrink: 0,
              }}
            />

            <Typography
              variant="subtitle2"
              noWrap
              sx={{ flex: 1, fontWeight: 700 }}
            >
              {device?.name || `#${deviceId}`}
            </Typography>

            <Tooltip title="Ver mais detalhes">
              <IconButton
                size="small"
                aria-label="Ver mais detalhes"
                onClick={(e) => {
                  e.stopPropagation();
                  onExpand?.();
                }}
              >
                <ExpandLessIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Fechar">
              <IconButton
                size="small"
                aria-label="Ver mais detalhes"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose?.();
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: state.color,
                minWidth: 0,
                flex: 1,
              }}
            >
              {state.icon}
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                {state.label}
              </Typography>
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              {speedLabel}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
              <Tooltip title={locked ? 'Bloqueado' : 'Desbloqueado'}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {locked ? (
                    <LockIcon sx={{ color: 'error.main', fontSize: 18 }} />
                  ) : (
                    <LockOpenIcon sx={{ color: 'success.main', fontSize: 18 }} />
                  )}
                </Box>
              </Tooltip>

              {battery != null && (
                <Tooltip title={`Bateria ${battery}%`}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BatteryIcon
                      level={battery}
                      charging={charging}
                      color={battery > 30 ? '#28A745' : '#DC3545'}
                    />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.75,
            flexWrap: 'wrap',
          }}
        >
          <QuickAction
            title={t('commandTitle')}
            icon={<SendIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate(`/settings/device/${deviceId}/command`)}
          />

          <QuickAction
            title={t('reportTitle')}
            icon={<RouteIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate(`/replay?deviceId=${deviceId}`)}
          />

          <QuickAction
            title={t('mapTitle')}
            icon={<MapIcon sx={{ fontSize: 18 }} />}
            onClick={(e) => setMapsAnchorEl(e.currentTarget)}
          />
        </Box>
      </Box>

      <Menu
        anchorEl={mapsAnchorEl}
        open={Boolean(mapsAnchorEl)}
        onClose={() => setMapsAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setMapsAnchorEl(null);
            openMapLink(links?.googleMaps, 'google_maps');
          }}
        >
          <ListItemIcon>
            <LocationOnIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Google Maps"
            secondary="Abrir localização no Google Maps"
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setMapsAnchorEl(null);
            openMapLink(links?.appleMaps, 'apple_maps');
          }}
        >
          <ListItemIcon>
            <LocationOnIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Apple Maps"
            secondary="Abrir localização no Apple Maps"
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setMapsAnchorEl(null);
            openMapLink(links?.streetView, 'street_view');
          }}
        >
          <ListItemIcon>
            <ExploreIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Street View"
            secondary="Abrir visão da rua"
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setMapsAnchorEl(null);
            openMapLink(links?.waze, 'waze');
          }}
        >
          <ListItemIcon>
            <MyLocationIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Waze"
            secondary="Navegar até o veículo"
          />
        </MenuItem>

        <MenuItem
          onClick={async () => {
            setMapsAnchorEl(null);
            await copyCoords();
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Copiar coordenadas"
            secondary="Latitude e longitude"
          />
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(failedMapUrl)}
        onClose={() => setFailedMapUrl('')}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Não foi possível abrir o app de mapas</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Abra o link manualmente:
          </Typography>
          <Link href={failedMapUrl} target="_blank" rel="noopener noreferrer">
            {failedMapUrl}
          </Link>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              const ok = await safeClipboardWrite(`${position?.latitude},${position?.longitude}`);
              setToast(ok ? 'Coordenadas copiadas' : 'Não foi possível copiar');
            }}
          >
            Copiar coordenadas
          </Button>
          <Button
            onClick={async () => {
              const ok = await safeClipboardWrite(failedMapUrl);
              setToast(ok ? 'URL copiada' : 'Não foi possível copiar');
            }}
          >
            Copiar URL
          </Button>
          <Button onClick={() => setFailedMapUrl('')}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        message={toast}
        onClose={() => setToast('')}
      />
    </>
  );
};

export default MobileDeviceBar;
